import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import useQuery from '../../../utils/useQuery';
import { http } from '../../../utils/http';
import { genericErrorMessage } from '../../../utils/lang/commonErrors';

import SubmitButton from '../fields/SubmitButton';

import styles from '../ReferralSignUpForm.scss';

const ReferralCodeForm = ({ handleSubmit, disabled }) => {
  const { code } = useQuery();

  const intl = useIntl();

  const [isLoading, setLoading] = useState(false);

  const [codeValue, setCodeValue] = useState(code || '');
  const onChange = (e) => setCodeValue(e.target.value);

  const [codeError, setCodeError] = useState('');

  const onSubmit = (e) => {
    e?.preventDefault();
    setCodeError('');
    setLoading(true);
    if (codeValue.length === 0) {
      setCodeError(intl.formatMessage({
        id: 'refSignUp.codeForm.error.codeInputEmpty',
        defaultMessage: 'Please, enter the code',
        description: 'Error message, if user tried to submit a form w/o inputting referral code'
      }));
      setLoading(false);
    } else {
      http('/v1/account/check-referral', 'POST', {
        'referral_id': codeValue
      })
        .then((res) => {
          if (res.exist) {
            setLoading(false);
            handleSubmit(codeValue);
          } else if (typeof res.exist === 'boolean' && !res.exist) {
            setLoading(false);
            setCodeError(intl.formatMessage({
              id: 'refSignUp.codeForm.error.codeIsOverLimit',
              defaultMessage: 'Code application limit exceeded',
              description: 'Error message, if the referral code is applied to maximum number of sign ups (five at the time of writing)'
            }));
          } else if (res.code === 5) {
            setLoading(false);
            setCodeError(intl.formatMessage({
              id: 'refSignUp.codeForm.error.invalidCode',
              defaultMessage: 'This code doesn’t exist',
              description: 'Error message, if the referral code does not exist'
            }));
          } else {
            setLoading(false);
            setCodeError(intl.formatMessage({
              id: 'refSignUp.codeForm.error.fallbackError',
              defaultMessage: 'Code is invalid or usage limit is reached',
              description: 'Fallback error message for code form'
            }));
          }
        })
        .catch((err) => {
          console.error(err);
          setLoading(false);
          setCodeError(intl.formatMessage(genericErrorMessage));
        });
    }
  };

  useEffect(() => {
    // auto-applying code if it's present in the params
    if (code) {
      onSubmit();
    }
  }, []);

  return (
    <form className={styles.form} onSubmit={onSubmit}>
      <fieldset disabled={disabled || isLoading}>
        <h2 className={styles.formTitle}>
          <FormattedMessage
            id="refSignUp.codeForm.title"
            defaultMessage="Activate your code"
            description="Title of the referral code form"
          />
        </h2>

        <label className={`${styles.label} ${codeError ? styles.error : ''}`}>
          <FormattedMessage
            id="refSignUp.codeForm.label"
            defaultMessage="Enter your code"
            description="Label of the referral code input"
          />

          <div className={styles.inputWrapper}>
            <input
              type="text"
              value={codeValue}
              onChange={onChange}
              placeholder={intl.formatMessage({
                id: 'refSignUp.codeForm.inputPlaceholder',
                defaultMessage: 'Your referral code',
                description: 'Placeholder for the referral code input'
              })}
            />
            <SubmitButton
              className={styles.inFieldButton}
              text={intl.formatMessage({
                id: 'refSignUp.codeForm.cta',
                defaultMessage: 'Activate',
                description: 'Submit button of the referral code form'
              })}
              isLoading={isLoading}
              loaderProps={{ size: 16, stroke: 2 }}
            />
          </div>

          <span className={styles.errorText}>{codeError}</span>
        </label>
      </fieldset>
    </form>
  );
};

export default ReferralCodeForm;
